import React from "react";
import { GlobeAltIcon, XIcon } from "@heroicons/react/outline";

/**
 * Modal that prompts user to sync their lineup to the global competition.
 * Supports two modes: creating a new global submission, or updating an existing one.
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {Function} props.onClose - Handler for closing modal (user says no)
 * @param {Function} props.onConfirm - Handler for confirming sync (user says yes)
 * @param {boolean} props.isSubmitting - Whether submission is in progress
 * @param {boolean} props.isUpdate - Whether this is syncing changes to an existing global lineup
 */
const GlobalLineupSyncModal = ({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting,
  isUpdate = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-2 sm:p-4">
      <div
        className="fixed inset-0 bg-dark-grey bg-opacity-75 transition-opacity"
        onClick={!isSubmitting ? onClose : undefined}
      ></div>

      <div className="relative transform overflow-hidden rounded-t-2xl sm:rounded-xl bg-white text-left shadow-2xl transition-all w-full max-w-sm sm:max-w-md mx-auto max-h-[90vh] sm:max-h-[85vh] flex flex-col mb-safe">
        {/* Header */}
        <div className="bg-gradient-to-r from-deep-red to-bright-red text-white p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <GlobeAltIcon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold">Global Competition</h3>
                <p className="text-white/80 text-sm">
                  {isUpdate
                    ? "Sync your lineup changes"
                    : "Submit your lineup globally"}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors text-white disabled:opacity-50"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4">
          <div className="text-center mb-2">
            <div className="w-16 h-16 bg-deep-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <GlobeAltIcon className="w-8 h-8 text-deep-red" />
            </div>
            <h4 className="text-lg font-semibold text-dark-grey mb-2">
              {isUpdate
                ? "Sync Changes to Global Competition?"
                : "Submit to Global Competition?"}
            </h4>
            <p className="text-medium-grey text-sm leading-relaxed">
              {isUpdate
                ? "Your Global Competition lineup is different from the changes you just made. Would you like to update your Global Competition lineup to match?"
                : "Would you like to submit this same lineup to the Global Competition as well? Compete against all players worldwide."}
            </p>
          </div>
        </div>

        {/* Fixed Actions Footer */}
        <div className="p-4 bg-light-grey border-t border-gray-200 flex-shrink-0">
          <div className="space-y-3">
            <button
              type="button"
              onClick={onConfirm}
              disabled={isSubmitting}
              className="w-full bg-deep-red hover:bg-bright-red text-white py-4 px-4 rounded-xl text-base font-bold transition-colors min-h-[48px] flex items-center justify-center shadow-sm disabled:opacity-50"
            >
              {isSubmitting
                ? "Submitting..."
                : isUpdate
                  ? "Yes, Sync Changes"
                  : "Yes, Submit to Global"}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="w-full bg-white text-dark-grey py-3 px-4 rounded-xl text-sm font-medium border border-light-grey hover:bg-gray-50 transition-colors min-h-[44px] flex items-center justify-center disabled:opacity-50"
            >
              No Thanks
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalLineupSyncModal;
